import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

export const AuthContext = createContext(null);

// --- SSO TOKEN CAPTURE ENGINE ---
const captureSSOToken = () => {
  try {
    const url = new URL(window.location.href);
    // console.log('[CMS SSO] Checking URL for token:', window.location.href);
    
    // 1. Check Search Params
    let token = url.searchParams.get('sso_token');
    // if (token) console.log('[CMS SSO] Found token in searchParams:', token.substring(0, 10) + '...');
    
    // 2. Check Hash
    if (!token && window.location.hash) {
      // console.log('[CMS SSO] Checking hash:', window.location.hash);
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : hash);
      token = params.get('sso_token');
      // if (token) console.log('[CMS SSO] Found token in hash:', token.substring(0, 10) + '...');
    }

    if (token) {
      // console.log('[CMS SSO] Token captured successfully, saving to localStorage');
      localStorage.setItem('cms_token', token);
      return token;
    }
  } catch (e) {
    // console.error('[CMS SSO] CAPTURE ERROR:', e);
  }
  
  const storedToken = localStorage.getItem('cms_token');
  // if (storedToken) console.log('[CMS SSO] Using stored token from localStorage');
  return storedToken;
};

const globalInitialToken = captureSSOToken();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(globalInitialToken);

  useEffect(() => {
    // console.log('[CMS Auth] Effect triggered, token exists:', !!token);
    if (!token) {
      // One last check just in case it appeared late
      const lateToken = captureSSOToken();
      if (lateToken) {
        // console.log('[CMS Auth] Token found late, updating state');
        setToken(lateToken);
        return;
      }
      // console.log('[CMS Auth] No token found, setting loading to false');
      setLoading(false);
      return;
    }

    // Verify token with backend
    // console.log('[CMS Auth] Verifying token with /auth/me...');
    api.get('/auth/me')
      .then(res => {
        // console.log('[CMS Auth] Verification SUCCESS:', res.data);
        const userData = res.data.user || res.data;
        setUser(userData);
        localStorage.setItem('cms_user', JSON.stringify(userData));
        
        // Clean URL after successful verification
        if (window.location.href.includes('sso_token')) {
          // console.log('[CMS Auth] Cleaning SSO token from URL');
          setTimeout(() => {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }, 1000);
        }
      })
      .catch((err) => {
        // console.error('[CMS Auth] Verification FAILED:', err.response?.data || err.message);
        localStorage.removeItem('cms_token');
        localStorage.removeItem('cms_user');
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('cms_token', data.token);
    localStorage.setItem('cms_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('cms_token');
    localStorage.removeItem('cms_user');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user && !!token }}>
      {children}
    </AuthContext.Provider>
  );
}
